import { useCallback } from 'react'
import { useNavigate, useParams, type NavigateOptions } from 'react-router'
import { useTranslation } from 'react-i18next'

/**
 * Supported languages for URL prefixing
 */
export const SUPPORTED_LANGS = ['en', 'fr'] as const
export type SupportedLang = (typeof SUPPORTED_LANGS)[number]

/**
 * Hook that wraps useNavigate to automatically add language prefix to paths.
 *
 * Usage:
 * ```typescript
 * const navigate = useLocalizedNavigate()
 *
 * // Absolute paths get prefixed with current language
 * navigate('/dashboard')  // → /fr/dashboard (if lang = fr)
 *
 * // Relative paths remain unchanged
 * navigate('../settings') // → ../settings
 *
 * // History navigation works as expected
 * navigate(-1)            // → back
 *
 * // Query params are preserved
 * navigate('/dashboard?tab=billing') // → /fr/dashboard?tab=billing
 * ```
 */
export function useLocalizedNavigate() {
  const navigate = useNavigate()
  const { lang } = useParams<{ lang: string }>()
  const { i18n } = useTranslation()

  // Determine current language: URL param > i18n instance > fallback 'en'
  const currentLang = lang || i18n.language || 'en'

  // Validate language is supported, fallback to 'en' if not
  const validLang: SupportedLang = SUPPORTED_LANGS.includes(
    currentLang as SupportedLang
  )
    ? (currentLang as SupportedLang)
    : 'en'

  return useCallback(
    (to: string | number, options?: NavigateOptions) => {
      // History navigation (back/forward) - pass through
      if (typeof to === 'number') {
        return navigate(to)
      }

      // Relative paths (starting with . or ..) - no prefix
      if (to.startsWith('.') || to.startsWith('..')) {
        return navigate(to, options)
      }

      // Absolute paths - add language prefix
      if (to.startsWith('/')) {
        // Check if already prefixed with any supported language
        const langPrefixRegex = new RegExp(`^/(${SUPPORTED_LANGS.join('|')})(/|$)`)
        if (langPrefixRegex.test(to)) {
          // Already has a language prefix, navigate as-is
          return navigate(to, options)
        }

        // Add current language prefix
        return navigate(`/${validLang}${to}`, options)
      }

      // Other cases (rare) - pass through
      return navigate(to, options)
    },
    [navigate, validLang]
  )
}
